import { useParams } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { Box, Button, Flexbox, Modal } from '@hiiretail/synergy-ui';
import { CancelButton } from '../../../../../GroupDetails/ui/modals/AddBusinessUnitModal/AddBusinessUnitModal.styles';
import { Option } from '@hiiretail/synergy-ui/dist/types/components/inputs/Select/Select.types';
import { AttributeSelect } from './AddCustomRoleAttributeModal.styles';
import { UpdateCustomRoleMutationArgs } from '../../../../hooks/useUpdateCustomRole';
import { AddCustomRoleAttributeModalComponent } from './AddCustomRoleAttributeModal.types';
import { PosAttributes } from '../../../../../../common/types/posAttributes';
import { getPosAttributes } from '../../../../../../common/utils/posAttributes';
import { useGetCustomRoles } from 'pages/CustomRoles/hooks/useGetCustomRoles';

export const AddCustomRoleAttributeModal: AddCustomRoleAttributeModalComponent = ({
  open,
  onClose,
  customRole,
  selectedPermission,
  addAttribute,
  isAdding,
}) => {
  const { id } = useParams<{ id: string }>();
  const [selectedAttribute, setSelectedAttribute] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const [selectedCustomRole, setSelectedCustomRole] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const {
    data: customRoles,
    isFetching: isCustomRolesLoading,
    error: customRolesError,
  } = useGetCustomRoles({
    cacheTime: 50000,
    staleTime: Infinity,
    keepPreviousData: false,
    retry: false,
  });

  const options = useMemo(
    () =>
      Object.entries(PosAttributes)
        ?.filter(
          ([attribute]) =>
            !selectedPermission?.attributes ||
            // @ts-ignore
            !selectedPermission.attributes[attribute],
        )
        .map(([attribute, displayName]) => ({
          value: attribute,
          label: displayName,
        })),
    [selectedPermission],
  );

  const customRolesOptions: Option[] = useMemo(() => {
    if (!customRoles) {
      return [];
    }

    return customRoles.map((role) => ({
      value: role.id,
      label: role.name ?? '',
    }));
  }, [customRoles]);

  const isManagerOverrideOrBackout = useMemo(() => {
    return (
      selectedAttribute?.label === PosAttributes.manager_override ||
      selectedAttribute?.label === PosAttributes.manager_override_with_backout
    );
  }, [selectedAttribute]);

  const onChange = useCallback(
    (newOption) => {
      setSelectedAttribute(newOption);
    },
    [setSelectedAttribute],
  );

  const onCustomRoleChange = useCallback(
    (newOption) => {
      setSelectedCustomRole(newOption);
    },
    [setSelectedCustomRole],
  );

  const onSubmit = useCallback(() => {
    if (!selectedPermission || !selectedAttribute) {
      return;
    }

    const updatedAttributes = {
      ...selectedPermission.attributes,
      ...getPosAttributes(
        selectedAttribute.label as PosAttributes,
        isManagerOverrideOrBackout ? selectedCustomRole?.value : undefined,
      ),
    };

    const updatedPermission = {
      ...selectedPermission,
      attributes: updatedAttributes,
    };

    const updatedPermissions = customRole.permissions.map((permission) =>
      permission.id === selectedPermission.id
        ? { ...updatedPermission, alias: undefined }
        : { ...permission, alias: undefined },
    );

    const data: UpdateCustomRoleMutationArgs = {
      id,
      updateCustomRoleDto: {
        name: customRole.name,
        permissions: updatedPermissions,
      },
    } as UpdateCustomRoleMutationArgs;

    addAttribute(data);
  }, [
    selectedPermission,
    selectedAttribute,
    isManagerOverrideOrBackout,
    selectedCustomRole?.value,
    customRole.permissions,
    customRole.name,
    id,
    addAttribute,
  ]);

  return (
    <Modal
      data-testid="add-attribute-modal"
      title="Add attribute"
      onClose={onClose}
      open={open}
      footer={
        <Flexbox container justifyContent="flex-end">
          <CancelButton disabled={isAdding} onClick={onClose} variant="secondary">
            Cancel
          </CancelButton>
          <Button disabled={isAdding || !selectedAttribute} onClick={onSubmit} type="submit">
            Submit
          </Button>
        </Flexbox>
      }
    >
      <Box style={{ height: '460px' }}>
        <AttributeSelect
          options={options as unknown as Option[]}
          name="attributes"
          label="Select an attribute to add"
          onChange={onChange}
          fullWidth
        />
        {isManagerOverrideOrBackout && !isCustomRolesLoading && !customRolesError ? (
          <AttributeSelect
            options={customRolesOptions}
            name="customRoles"
            label="Select custom role to add"
            onChange={onCustomRoleChange}
            fullWidth
          />
        ) : null}
      </Box>
    </Modal>
  );
};
